import { X, Upload } from 'lucide-react';
import { useState } from 'react';

interface AvatarPersonaVideoFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AvatarPersonaVideoForm({ isOpen, onClose }: AvatarPersonaVideoFormProps) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [objective, setObjective] = useState('');
  const [script, setScript] = useState('');
  const [voiceSelection, setVoiceSelection] = useState<'female' | 'male' | ''>('');
  const [voiceSample, setVoiceSample] = useState<File | null>(null);
  const [portraitUpload, setPortraitUpload] = useState<File | null>(null);
  const [avatarSetting, setAvatarSetting] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState<'venmo' | 'paypal'>('venmo');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [mailingAddress, setMailingAddress] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('United States');
  const [zipCode, setZipCode] = useState('');

  const wordCount = script.trim().split(/\s+/).filter(word => word.length > 0).length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!fullName || !email || !objective || wordCount < 20 || !voiceSelection || !portraitUpload || !agreedToTerms) {
      alert('Please fill in all required fields');
      return;
    }

    // Save order to localStorage
    const order = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      product: 'Avatar Basic Template',
      price: 24,
      fullName,
      email,
      organization,
      objective,
      script,
      voiceSelection,
      avatarSetting,
      status: 'Completed'
    };

    const existingOrders = JSON.parse(localStorage.getItem('avatarOrders') || '[]');
    localStorage.setItem('avatarOrders', JSON.stringify([...existingOrders, order]));

    alert('Order submitted successfully! Check your Order History.');
    onClose();
    
    // Reset form
    setFullName('');
    setEmail('');
    setOrganization('');
    setObjective('');
    setScript('');
    setVoiceSelection('');
    setVoiceSample(null);
    setPortraitUpload(null);
    setAvatarSetting('');
    setAgreedToTerms(false);
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardholderName('');
    setMailingAddress('');
    setState('');
    setZipCode('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[120] p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-[700px] max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 p-4 sm:p-6 rounded-t-2xl z-10 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="size-4 text-white" />
          </button>
          
          <div className="pr-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">Create Your AI Avatar</h2>
            <p className="text-sm text-white/90">One-time payment • No subscription required</p>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6 space-y-6">
          {/* Pricing Header */}
          <div className="bg-white border-2 border-gray-300 rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Pro Level 1</p>
                <p className="text-xs text-gray-700 font-semibold mb-3">No monthly commitment</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Avatar Template</h3>
                <p className="text-2xl font-bold text-gray-900 mb-3">$24<span className="text-sm font-normal text-gray-600">/one-time</span></p>
                <p className="text-sm font-semibold text-gray-700 mb-2">15 second custom AI avatar creation</p>
                <ul className="space-y-1">
                  <li className="text-xs text-gray-700">• Your script (20-30 words)</li>
                  <li className="text-xs text-gray-700">• No revisions</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Your Name */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Avatar Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="BIGMIKE2"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 text-gray-900"
                required
              />
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Email Address <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 text-gray-900"
                required
              />
            </div>

            {/* Organization */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Organization <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="Your Organization Name"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 text-gray-900"
                required
              />
            </div>

            {/* Objective */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Objective <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                placeholder="Tell what you want viewers to learn or do."
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 text-gray-900"
                required
              />
            </div>

            {/* Script */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Script (20 - 30 words) <span className="text-red-600">*</span>
              </label>
              <textarea
                value={script}
                onChange={(e) => setScript(e.target.value)}
                placeholder="Your video will communicate this exact script so check it twice!"
                rows={4}
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 text-gray-900"
                required
              />
              <p className={`text-xs mt-1 ${wordCount < 20 ? 'text-red-600' : 'text-gray-600'}`}>
                {wordCount} words (minimum 20)
              </p>
            </div>

            {/* Voice Selection */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Voice Selection (choose one) <span className="text-red-600">*</span>
              </label>
              <p className="text-xs text-gray-600 mb-3">These are Avatars - tap one to select the voice for your video.</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setVoiceSelection('female')}
                  className={`px-4 py-3 border-2 rounded-lg font-semibold transition-all ${
                    voiceSelection === 'female'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 hover:border-gray-400 text-gray-700'
                  }`}
                >
                  Female Voice
                </button>
                <button
                  type="button"
                  onClick={() => setVoiceSelection('male')}
                  className={`px-4 py-3 border-2 rounded-lg font-semibold transition-all ${
                    voiceSelection === 'male'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 hover:border-gray-400 text-gray-700'
                  }`}
                >
                  Male Voice
                </button>
              </div>
            </div>

            {/* Voice Sample Upload */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Upload 30 Second Voice Sample (optional: when a sample voice file is not attached a voice is automatically assigned)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept=".mp3,.mp4,.wav,.m4a"
                  onChange={(e) => setVoiceSample(e.target.files?.[0] || null)}
                  className="hidden"
                  id="voice-upload"
                />
                <label htmlFor="voice-upload" className="cursor-pointer">
                  <Upload className="size-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {voiceSample ? voiceSample.name : 'No file chosen'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Upload a 30 second voice sample for voice cloning (MP3, MP4, WAV, M4A)</p>
                </label>
              </div>
            </div>

            {/* Portrait Upload */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Portrait Upload <span className="text-red-600">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={(e) => setPortraitUpload(e.target.files?.[0] || null)}
                  className="hidden"
                  id="portrait-upload"
                  required
                />
                <label htmlFor="portrait-upload" className="cursor-pointer">
                  <Upload className="size-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {portraitUpload ? portraitUpload.name : 'No file chosen'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Upload a quality portrait photo facing forward in PNG, or JPG.</p>
                </label>
              </div>
            </div>

            {/* Avatar Setting */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-900">
                Avatar Setting <span className="text-red-600">*</span>
              </label>
              <textarea
                value={avatarSetting}
                onChange={(e) => setAvatarSetting(e.target.value)}
                placeholder="Describe the setting you want to see the avatar in (e.g., modern office, outdoor park, professional studio)"
                maxLength={256}
                rows={3}
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 text-gray-900"
                required
              />
              <p className="text-xs mt-1 text-gray-600">
                {avatarSetting.length}/256 characters
              </p>
            </div>

            {/* Ethical Agreement */}
            <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-4">
              <h3 className="text-sm font-bold mb-3 text-gray-900">Ethical and Moral Agreement</h3>
              <p className="text-xs text-gray-700 mb-3">
                Check the box to indicate you're 18 years of age or older. This video will be used for positive messaging and information integrity. You will not use this to cyber bully, intimidate, incite violence or share private information that could cause damages.
              </p>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 size-[4.4] accent-purple-600"
                  required
                />
                <span className="text-sm text-gray-900">
                  I agree to the Ethical and Moral Agreement <span className="text-red-600">*</span>
                </span>
              </label>
            </div>

            {/* Payment Details */}
            <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-4">
              <h3 className="text-lg font-bold mb-4 text-gray-900">Payment Details</h3>
              
              {/* Payment Method */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2 text-gray-900">
                  Select Payment Method <span className="text-red-600">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('venmo')}
                    className={`px-4 py-3 border-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      paymentMethod === 'venmo'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                    }`}
                  >
                    <span className="text-xl">💳</span>
                    <span className="text-blue-600">Venmo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    className={`px-4 py-3 border-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      paymentMethod === 'paypal'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                    }`}
                  >
                    <span className="text-xl">🅿️</span>
                    PayPal
                  </button>
                </div>
              </div>

              {paymentMethod === 'venmo' && (
                <>
                  <p className="text-xs text-gray-600 mb-4">You'll be redirected to Venmo to complete your purchase securely.</p>
                </>
              )}
            </div>

            {/* Submit Button */}
            <a
              href="https://venmo.com/ORA-AI"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center no-underline hover:no-underline"
              style={{ boxShadow: '0 0 28px rgba(59, 130, 246, 0.7), 0 0 100px rgba(59, 130, 246, 0.35), 0 0 260px rgba(147, 51, 234, 0.10), 0 0 340px rgba(255, 255, 255, 0.10), 0 8px 16px rgba(0, 0, 0, 0.4)' }}
            >
              Pay $24 Now
            </a>
            <p className="text-xs text-center text-gray-600">🔒 Secured by 256-bit SSL encryption</p>
          </form>
        </div>
      </div>
    </div>
  );
}